import React, { useCallback, useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { RELATIONSHIPS_EVENT_NAMES } from '../../../../analytics/event-name';
import useAnalytics from '../../../../analytics/useAnalytics';
import type { RelationshipT } from '../../../../modernization-common/relationships';
import { getRelationshipBasePayload } from '../../../../modernization-common/relationships/analytics/utils';
import { dispatchAtom } from '../../atoms/label-store-dispatch/dispatch';
import { UNDOABLE_EVENTS } from '../../atoms/label-store-dispatch/events';
import { editingRelationshipAtom, isEditingAtom, isSourceEditableAtom, sourceObjectIdAtom } from '../../atoms/relationship-atoms';
import { RelationshipEditorPanel } from './editor-components/relationship-editor-panel';
type RelationshipEditorT = {
  onClose?: () => void;
  isVideoTask: boolean;
};
export const RelationshipEditor = ({
  onClose,
  isVideoTask
}: RelationshipEditorT) => {
  const sourceObjectId = useAtomValue(sourceObjectIdAtom);
  const isSourceEditable = useAtomValue(isSourceEditableAtom);
  const dispatch = useSetAtom(dispatchAtom);
  const isEditing = useAtomValue(isEditingAtom);
  const editingRelationship = useAtomValue(editingRelationshipAtom);
  const {
    sendEvent
  } = useAnalytics();
  const sendRelationshipEvent = useCallback((relationships: RelationshipT[]) => {
    relationships.forEach(relationship => {
      const basePayload = getRelationshipBasePayload(relationship);
      if (isEditing && editingRelationship) {
        const updatedFields: string[] = [];
        if (relationship.relationshipLabel.name !== editingRelationship.relationshipLabel.name) {
          updatedFields.push('relationshipPredicateName');
        }
        if (relationship.targetAnnotationId !== editingRelationship.targetAnnotationId) {
          updatedFields.push('targetObjectId');
        }
        if (JSON.stringify(relationship.frameRanges) !== JSON.stringify(editingRelationship.frameRanges)) {
          updatedFields.push('frameRanges');
        }
        sendEvent({
          action: RELATIONSHIPS_EVENT_NAMES.UPDATE_RELATIONSHIP,
          payload: {
            ...basePayload,
            updatedFields
          }
        });
      } else {
        sendEvent({
          action: RELATIONSHIPS_EVENT_NAMES.ADD_RELATIONSHIP,
          payload: basePayload
        });
      }
    });
  }, [isEditing, editingRelationship, sendEvent]);
  const handleSave = useCallback((relationships: RelationshipT[]) => {
    sendRelationshipEvent(relationships);
    dispatch({
      type: UNDOABLE_EVENTS.SAVE_RELATIONSHIPS,
      relationships
    });
    onClose?.();
  }, [dispatch, onClose, sendRelationshipEvent]);
  const handleCancel = useCallback(() => {
    onClose?.();
  }, [onClose]);
  useEffect(() => {
    if (!sourceObjectId && !isSourceEditable) {
      onClose?.();
    }
  }, [sourceObjectId, isSourceEditable, onClose]);
  if (!sourceObjectId && !isSourceEditable) {
    return null;
  }
  return <RelationshipEditorPanel onSave={handleSave} onCancel={handleCancel} isVideoTask={isVideoTask} />;
};